import { canonicalClinicalCode } from "./normalise";

declare const canonicalIcd10: unique symbol;
declare const canonicalOpcs4: unique symbol;

/**
 * A code in the single form an exact comparison must be made in — trimmed, uppercase and dotted
 * from the 4th character. Nothing more: the code need not exist in `icd10_codes`. A branded value
 * is safe to compare against a dotted literal and to index a category from; it is not evidence the
 * code is assignable.
 *
 * `ConfirmedIcd10Code` in `@smartmed/effect-clinical-codes` is the stronger claim, and extends this.
 */
export type CanonicalIcd10Code = string & { readonly [canonicalIcd10]: true };

/** The OPCS-4 counterpart of {@link CanonicalIcd10Code}, disjoint from it so the two cannot swap. */
export type CanonicalOpcs4Code = string & { readonly [canonicalOpcs4]: true };

/**
 * A chapter letter, a two-digit category, and at most one subdivision — the shape both
 * classifications share. The subdivision's first character may be a letter because NHS ICD-10 uses
 * `X` as the 4th-character filler (`M45.X0`, `T08.X1`).
 */
const CANONICAL_CLINICAL_CODE = /^[A-Z]\d{2}(\.[0-9A-Z]\d{0,3})?$/;

/** Whether a code is already in the form {@link canonicalClinicalCode} produces, and shaped like a code. */
export const isCanonicalClinicalCode = (code: string): boolean =>
  code === canonicalClinicalCode(code) && CANONICAL_CLINICAL_CODE.test(code);

/**
 * The canonical form of a code, or `undefined` where the input is not a code at all. Non-throwing
 * because a reviewer mid-keystroke holds `K2` for as long as it takes to type the rest, and a feed
 * sends whatever it sends.
 */
export const parseCanonicalIcd10Code = (code: string): CanonicalIcd10Code | undefined => {
  const canonical = canonicalClinicalCode(code);
  return isCanonicalClinicalCode(canonical) ? (canonical as CanonicalIcd10Code) : undefined;
};

export const parseCanonicalOpcs4Code = (code: string): CanonicalOpcs4Code | undefined => {
  const canonical = canonicalClinicalCode(code);
  return isCanonicalClinicalCode(canonical) ? (canonical as CanonicalOpcs4Code) : undefined;
};
